/**
 * Centralized Query Key Factory
 *
 * Single source of truth for all React Query cache keys across the application.
 * Every key is scoped by `tenantId` to ensure proper cache isolation between tenants
 * (ISO 27001 A.9 — access control / data segregation).
 *
 * ## Key Hierarchy (supports granular invalidation)
 *
 * ```
 * entity
 *   └── [tenantId]              ← all(tenantId)       → invalidates everything for this entity+tenant
 *         ├── "list"            ← lists(tenantId)      → invalidates all list variants
 *         │     └── {filters}   ← list(tenantId, f)    → one specific filtered list
 *         ├── "detail"          ← details(tenantId)    → invalidates all detail entries
 *         │     └── id          ← detail(tenantId, id) → one specific record
 *         └── ...sub-keys
 * ```
 *
 * ## Usage
 *
 * ```tsx
 * import { queryKeys } from '../api/queryKeys';
 * import { useTenantId } from '../hooks/useTenantId';
 *
 * function MyComponent() {
 *   const tenantId = useTenantId();
 *   const { data } = useQuery({
 *     queryKey: queryKeys.cases.detail(tenantId, caseId),
 *     queryFn: () => casesApi.getCase(caseId),
 *   });
 * }
 * ```
 *
 * ## Invalidation
 *
 * ```tsx
 * // Invalidate a single case detail
 * queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(tenantId, caseId) });
 *
 * // Invalidate all case lists (but keep detail cache)
 * queryClient.invalidateQueries({ queryKey: queryKeys.cases.lists(tenantId) });
 *
 * // Invalidate ALL case data for a tenant
 * queryClient.invalidateQueries({ queryKey: queryKeys.cases.all(tenantId) });
 * ```
 */

import type { CaseFilters } from "../types/case";
import type { RuleFilters } from "../api/rules";
import type { NodeResponsibilityFilters } from "../types/nodeResponsibility";
import type { DocumentFilters } from "../hooks/useDocuments";
import type { DocumentTypeListParams } from "../hooks/useDocumentTypes";
import type { PromptListParams } from "../hooks/usePrompts";
import type { ProcessingJobListFilters } from "../hooks/useProcessingJobs";
import type { AuditLogFilters } from "../types/auditLog";
import type { AssessmentTemplateListParams } from "./assessmentApi";

// ---------------------------------------------------------------------------
// Cases
// ---------------------------------------------------------------------------

const cases = {
  /** Root key for all case queries scoped to a tenant */
  all: (tenantId: number) => ["cases", tenantId] as const,
  /** All list-type case queries */
  lists: (tenantId: number) => [...cases.all(tenantId), "list"] as const,
  /** Case inbox with specific filters */
  inbox: (tenantId: number, filters: CaseFilters) =>
    [...cases.all(tenantId), "inbox", filters] as const,
  /** All detail-type case queries */
  details: (tenantId: number) => [...cases.all(tenantId), "detail"] as const,
  /** Single case by ID */
  detail: (tenantId: number, id: number) =>
    [...cases.details(tenantId), id] as const,
  /** Dashboard metrics */
  dashboard: (tenantId: number) =>
    [...cases.all(tenantId), "dashboard"] as const,
  /** Manager dashboard */
  managerDashboard: (tenantId: number) =>
    [...cases.all(tenantId), "manager-dashboard"] as const,
  /** Overdue cases */
  overdue: (tenantId: number) => [...cases.all(tenantId), "overdue"] as const,
  /** Cases due today */
  dueToday: (tenantId: number) =>
    [...cases.all(tenantId), "due-today"] as const,
  /** Cases due this week */
  dueThisWeek: (tenantId: number) =>
    [...cases.all(tenantId), "due-this-week"] as const,
  /** Allowed status transitions for a case */
  allowedTransitions: (tenantId: number, id: number) =>
    [...cases.all(tenantId), "allowed-transitions", id] as const,
  /** Documents explicitly linked to a case (case_documents table) */
  caseDocuments: (
    tenantId: number,
    caseId: number,
    params?: { page?: number; size?: number },
  ) => [...cases.detail(tenantId, caseId), "case-documents", params] as const,
} as const;

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

const rules = {
  all: (tenantId: number) => ["rules", tenantId] as const,
  lists: (tenantId: number) => [...rules.all(tenantId), "list"] as const,
  list: (tenantId: number, filters?: RuleFilters) =>
    [...rules.lists(tenantId), filters] as const,
  details: (tenantId: number) => [...rules.all(tenantId), "detail"] as const,
  detail: (tenantId: number, id: number) =>
    [...rules.details(tenantId), id] as const,
  chapters: (tenantId: number) => [...rules.all(tenantId), "chapters"] as const,
  children: (tenantId: number, parentId: number) =>
    [...rules.all(tenantId), "children", parentId] as const,
  history: (tenantId: number, ruleId: number) =>
    [...rules.all(tenantId), "history", ruleId] as const,
  relatedCases: (tenantId: number, ruleId: number) =>
    [...rules.all(tenantId), "cases", ruleId] as const,
  statistics: (tenantId: number) =>
    [...rules.all(tenantId), "statistics"] as const,
  search: (tenantId: number, query: string) =>
    [...rules.all(tenantId), "search", query] as const,
  roots: (tenantId: number) => [...rules.all(tenantId), "roots"] as const,
  tree: (tenantId: number) => [...rules.all(tenantId), "tree"] as const,
} as const;

// ---------------------------------------------------------------------------
// Rule Categories
// ---------------------------------------------------------------------------

const ruleCategories = {
  all: (tenantId: number) => ["ruleCategories", tenantId] as const,
  list: (tenantId: number) =>
    [...ruleCategories.all(tenantId), "list"] as const,
} as const;

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

const comments = {
  all: (tenantId: number) => ["comments", tenantId] as const,
  lists: (tenantId: number) => [...comments.all(tenantId), "list"] as const,
  list: (
    tenantId: number,
    caseId: number,
    params?: { page?: number; size?: number },
  ) => [...comments.lists(tenantId), caseId, params] as const,
  details: (tenantId: number) => [...comments.all(tenantId), "detail"] as const,
  detail: (tenantId: number, caseId: number, commentId: number) =>
    [...comments.details(tenantId), caseId, commentId] as const,
} as const;

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

const attachments = {
  all: (tenantId: number) => ["attachments", tenantId] as const,
  lists: (tenantId: number) => [...attachments.all(tenantId), "list"] as const,
  list: (tenantId: number, caseId: number) =>
    [...attachments.lists(tenantId), caseId] as const,
} as const;

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

const documents = {
  all: (tenantId: number) => ["documents", tenantId] as const,
  lists: (tenantId: number) => [...documents.all(tenantId), "list"] as const,
  list: (tenantId: number, filters: DocumentFilters) =>
    [...documents.lists(tenantId), filters] as const,
  details: (tenantId: number) =>
    [...documents.all(tenantId), "detail"] as const,
  detail: (tenantId: number, id: number) =>
    [...documents.details(tenantId), id] as const,
  linkedRules: (tenantId: number, documentId: number) =>
    [...documents.all(tenantId), "linked-rules", documentId] as const,
} as const;

// ---------------------------------------------------------------------------
// Document Types
// ---------------------------------------------------------------------------

const documentTypes = {
  all: (tenantId: number) => ["documentTypes", tenantId] as const,
  lists: (tenantId: number) =>
    [...documentTypes.all(tenantId), "list"] as const,
  list: (tenantId: number, params: DocumentTypeListParams) =>
    [...documentTypes.lists(tenantId), params] as const,
  details: (tenantId: number) =>
    [...documentTypes.all(tenantId), "detail"] as const,
  detail: (tenantId: number, id: number) =>
    [...documentTypes.details(tenantId), id] as const,
} as const;

// ---------------------------------------------------------------------------
// Prompts
// ---------------------------------------------------------------------------

const prompts = {
  all: (tenantId: number) => ["prompts", tenantId] as const,
  lists: (tenantId: number) => [...prompts.all(tenantId), "list"] as const,
  list: (tenantId: number, params: PromptListParams) =>
    [...prompts.lists(tenantId), params] as const,
  details: (tenantId: number) => [...prompts.all(tenantId), "detail"] as const,
  detail: (tenantId: number, id: number) =>
    [...prompts.details(tenantId), id] as const,
  global: (tenantId: number) => [...prompts.all(tenantId), "global"] as const,
} as const;

// ---------------------------------------------------------------------------
// Processing Jobs
// ---------------------------------------------------------------------------

const processingJobs = {
  all: (tenantId: number) => ["processing-jobs", tenantId] as const,
  lists: (tenantId: number) =>
    [...processingJobs.all(tenantId), "list"] as const,
  list: (tenantId: number, filters: ProcessingJobListFilters) =>
    [...processingJobs.lists(tenantId), filters] as const,
  details: (tenantId: number) =>
    [...processingJobs.all(tenantId), "detail"] as const,
  detail: (tenantId: number, id: number) =>
    [...processingJobs.details(tenantId), id] as const,
} as const;

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

const subscriptions = {
  all: (tenantId: number) => ["subscriptions", tenantId] as const,
  packages: (tenantId: number) =>
    [...subscriptions.all(tenantId), "packages"] as const,
  package: (tenantId: number, id: number) =>
    [...subscriptions.packages(tenantId), id] as const,
  company: (tenantId: number) =>
    [...subscriptions.all(tenantId), "company"] as const,
  companyByStatus: (tenantId: number, status?: number) =>
    [...subscriptions.company(tenantId), status] as const,
  companySubscription: (tenantId: number, subscriptionId: number) =>
    [...subscriptions.company(tenantId), subscriptionId] as const,
  gateways: (tenantId: number) =>
    [...subscriptions.all(tenantId), "gateways"] as const,
  management: (tenantId: number) =>
    [...subscriptions.all(tenantId), "subscription-management"] as const,
  managementList: (tenantId: number) =>
    [...subscriptions.management(tenantId), "list"] as const,
  managementPackages: (tenantId: number) =>
    [...subscriptions.management(tenantId), "packages"] as const,
  managementPackage: (tenantId: number, id: number) =>
    [...subscriptions.managementPackages(tenantId), id] as const,
  adminList: (
    tenantId: number,
    params?: {
      searchText?: string;
      status?: string;
      page?: number;
      pageSize?: number;
      sortBy?: string;
      sortDirection?: "ASC" | "DESC";
    },
  ) => [...subscriptions.management(tenantId), "admin-list", params] as const,
} as const;

// ---------------------------------------------------------------------------
// Node Responsibilities (Rule Default Assignments)
// ---------------------------------------------------------------------------

const nodeResponsibilities = {
  all: (tenantId: number) => ["nodeResponsibilities", tenantId] as const,
  lists: (tenantId: number) =>
    [...nodeResponsibilities.all(tenantId), "list"] as const,
  list: (tenantId: number, filters?: NodeResponsibilityFilters) =>
    [...nodeResponsibilities.lists(tenantId), filters] as const,
  details: (tenantId: number) =>
    [...nodeResponsibilities.all(tenantId), "detail"] as const,
  detail: (tenantId: number, ruleId: number) =>
    [...nodeResponsibilities.details(tenantId), ruleId] as const,
  byRole: (tenantId: number, roleId: number) =>
    [...nodeResponsibilities.all(tenantId), "role", roleId] as const,
  byUser: (tenantId: number, userId: number) =>
    [...nodeResponsibilities.all(tenantId), "user", userId] as const,
  effectiveAssignee: (tenantId: number, ruleId: number) =>
    [...nodeResponsibilities.all(tenantId), "effective", ruleId] as const,
  history: (tenantId: number, ruleId: number) =>
    [...nodeResponsibilities.all(tenantId), "history", ruleId] as const,
} as const;

// ---------------------------------------------------------------------------
// Questionnaires
// ---------------------------------------------------------------------------

const questionnaires = {
  all: (tenantId: number) => ["questionnaire", tenantId] as const,
  lists: (tenantId: number) =>
    [...questionnaires.all(tenantId), "list"] as const,
  list: (
    tenantId: number,
    filters?: {
      name?: string;
      status?: string;
      tenantId?: number | null;
      page?: number;
      pageSize?: number;
      sortBy?: string;
      sortDirection?: "ASC" | "DESC";
    },
  ) => [...questionnaires.lists(tenantId), filters] as const,
  detail: (tenantId: number, id: number) =>
    [...questionnaires.all(tenantId), "detail", id] as const,
} as const;

// ---------------------------------------------------------------------------
// Completed Questionnaires
// ---------------------------------------------------------------------------

const completedQuestionnaires = {
  all: (tenantId: number) => ["completedQuestionnaire", tenantId] as const,
  lastCompleted: (tenantId: number) =>
    [...completedQuestionnaires.all(tenantId), "lastCompleted"] as const,
  attempt: (tenantId: number, questionnaireId: number) =>
    [
      ...completedQuestionnaires.all(tenantId),
      "attempt",
      questionnaireId,
    ] as const,
} as const;

// ---------------------------------------------------------------------------
// Users (User Management)
// ---------------------------------------------------------------------------

const users = {
  all: (tenantId: number) => ["users", tenantId] as const,
  lists: (tenantId: number) => [...users.all(tenantId), "list"] as const,
  list: (
    tenantId: number,
    params: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: number;
    },
  ) => [...users.lists(tenantId), params] as const,
  details: (tenantId: number) => [...users.all(tenantId), "detail"] as const,
  detail: (tenantId: number, id: number) =>
    [...users.details(tenantId), id] as const,
  /** Users fetched for company-scoped dropdowns (assignment pickers, etc.) */
  byCompany: (tenantId: number, companyId?: string | number | null) =>
    [...users.all(tenantId), "company", companyId ?? null] as const,
} as const;

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

const roles = {
  all: (tenantId: number) => ["roles", tenantId] as const,
  lists: (tenantId: number) => [...roles.all(tenantId), "list"] as const,
  list: (
    tenantId: number,
    params: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: number;
    },
  ) => [...roles.lists(tenantId), params] as const,
  details: (tenantId: number) => [...roles.all(tenantId), "detail"] as const,
  detail: (tenantId: number, id: number) =>
    [...roles.details(tenantId), id] as const,
  entitlements: (tenantId: number, id: number) =>
    [...roles.detail(tenantId, id), "entitlements"] as const,
  users: (tenantId: number, id: number) =>
    [...roles.detail(tenantId, id), "users"] as const,
} as const;

// ---------------------------------------------------------------------------
// Tenants (admin-scoped — uses 'admin' as scope key, not tenant ID)
// ---------------------------------------------------------------------------

const tenants = {
  all: () => ["tenants", "admin"] as const,
  lists: () => [...tenants.all(), "list"] as const,
  list: (params: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string | number;
    sortBy?: string;
    sortDirection?: string;
  }) => [...tenants.lists(), params] as const,
  listDropdown: () => [...tenants.all(), "list", "dropdown"] as const,
  details: () => [...tenants.all(), "detail"] as const,
  detail: (id: number) => [...tenants.details(), id] as const,
  categories: () => [...tenants.all(), "categories"] as const,
  subcategories: () => [...tenants.all(), "subcategories"] as const,
  tenantSubcategories: (id: number) =>
    [...tenants.all(), "tenant-subcategories", id] as const,
  availableSubcategories: (id: number) =>
    [...tenants.all(), "tenant-subcategories", id, "available"] as const,
  logoUrl: (id: number) => [...tenants.detail(id), "logo-url"] as const,
} as const;

// ---------------------------------------------------------------------------
// Assessment Template (global — not tenant-scoped)
// ---------------------------------------------------------------------------

const assessment = {
  all: () => ["assessment"] as const,
  template: () => [...assessment.all(), "template"] as const,
  templates: (params?: AssessmentTemplateListParams) =>
    params
      ? ([...assessment.all(), "templates", params] as const)
      : ([...assessment.all(), "templates"] as const),
  templateDetail: (id: number) =>
    [...assessment.all(), "templates", "detail", id] as const,
  reports: (params?: { page?: number; pageSize?: number }) =>
    [...assessment.all(), "reports", params] as const,
} as const;

// ---------------------------------------------------------------------------
// Audit Logs
// ---------------------------------------------------------------------------

const auditLogs = {
  all: (tenantId: number) => ["audit-logs", tenantId] as const,
  lists: (tenantId: number) => [...auditLogs.all(tenantId), "list"] as const,
  list: (tenantId: number, filters: AuditLogFilters) =>
    [...auditLogs.lists(tenantId), filters] as const,
} as const;

// ---------------------------------------------------------------------------
// Stripe
// ---------------------------------------------------------------------------

const stripe = {
  all: (tenantId: number) => ["stripe", tenantId] as const,
  paymentStatus: (tenantId: number, paymentIntentId: string) =>
    [...stripe.all(tenantId), "payment-status", paymentIntentId] as const,
  config: () => ["stripe-config"] as const,
} as const;

// ---------------------------------------------------------------------------
// Payment Status (legacy polling key)
// ---------------------------------------------------------------------------

const payments = {
  status: (paymentId: string) => ["payment-status", paymentId] as const,
} as const;

// ===========================================================================
// Exported factory — single import for all query keys
// ===========================================================================

export const queryKeys = {
  cases,
  rules,
  ruleCategories,
  comments,
  attachments,
  documents,
  documentTypes,
  prompts,
  processingJobs,
  subscriptions,
  nodeResponsibilities,
  questionnaires,
  completedQuestionnaires,
  users,
  roles,
  tenants,
  assessment,
  stripe,
  payments,
  auditLogs,
} as const;

// ---------------------------------------------------------------------------
// List of all tenant-scoped entity root keys (used by invalidateAllTenantData)
// ---------------------------------------------------------------------------

/** Root key strings for all tenant-scoped entities */
export const TENANT_SCOPED_ENTITIES = [
  "cases",
  "rules",
  "ruleCategories",
  "comments",
  "attachments",
  "documents",
  "documentTypes",
  "prompts",
  "processing-jobs",
  "subscriptions",
  "nodeResponsibilities",
  "questionnaire",
  "completedQuestionnaire",
  "users",
  "roles",
  "stripe",
  "audit-logs",
] as const;
